/**
 * Pestaña «General» de la cabina de almacenamiento en variante tarjetas.
 *
 * Mismo formato que servidor y chasis (FICHA_ESTANDAR_TARJETAS §12.C4):
 * rejilla de dos columnas con tarjetas agrupadas.
 *  - Identificación | Ubicación
 *  - Gestión        | Tipo y protocolos
 *
 * Los campos se conectan al `handleChange` del formulario padre: no hay estado
 * duplicado.
 */
import React from 'react';
import { SparklesIcon } from '@heroicons/react/24/outline';
import LocationTreeSelect from './common/LocationTreeSelect';
import { CampoTexto, CampoSelect, opcionesDeCatalogo } from './common/Sf2Fields';
import { buildLocationMap, locationPath } from '../utils/locationTree';

const OPCIONES_ESTADO = [
  { value: 'active', label: 'Activo' },
  { value: 'maintenance', label: 'Mantenimiento' },
  { value: 'decom', label: 'Decom' },
];

const OPCIONES_ENTORNO = [
  { value: 'prod', label: 'Producción' },
  { value: 'pre', label: 'Pre-producción' },
  { value: 'dev', label: 'Desarrollo' },
];

const OPCIONES_TIPO = [
  { value: 'appliance', label: 'Cabina dedicada (appliance)' },
  { value: 'server_based', label: 'Servidor + Software (Linux, ZFS, Ceph…)' },
];

const OPCIONES_FORM_FACTOR = [
  { value: 'rack', label: 'Rack' },
  { value: 'tower', label: 'Torre' },
  { value: 'desktop', label: 'Desktop' },
];

export default function StorageFormSummary({
  form,
  editing = false,
  onChange,
  onNameSuggest,
  suggesting = false,
  manufacturerOptions = [],
  modelOptions = [],
  onManufacturerChange = null,
  onCreateManufacturer = null,
  onCreateModel = null,
  locations = [],
  racks = [],
  rackName = '',
  onLocationChange,
  onRackChange,
  rangeLabel = '',
  rackHeight = null,
  hasBmcComponent = false,
  canPromoteBmc = false,
  promotingBmc = false,
  onPromoteBmc,
  protocols = [],
  protocolColors = {},
  onToggleProtocol,
}) {
  const opcionesFabricante = opcionesDeCatalogo(manufacturerOptions);
  const opcionesModelo = opcionesDeCatalogo(modelOptions);
  const opcionesRack = [
    { value: '', label: '— Sin rack —' },
    ...racks.map((r) => ({ value: r.id, label: r.name || r.code })),
  ];
  if (form.rack_id && !opcionesRack.some((o) => String(o.value) === String(form.rack_id)) && rackName) {
    opcionesRack.push({ value: form.rack_id, label: rackName });
  }
  const protocolosActivos = Array.isArray(form.protocols) ? form.protocols : [];

  return (
    <div className="sf2-grid sf2-grid--2">
      <div className="sf2-card">
        <h3>Identificación<span className="sf2-count">editable</span></h3>
        <div className="sf2-fields">
          <div className="sf2-field">
            <label>Nombre</label>
            {editing ? (
              <div className="sf2-input-row">
                <input
                  name="name"
                  value={form.name || ''}
                  onChange={onChange}
                  placeholder="SAN-0001"
                />
                <button
                  type="button"
                  className="sf2-icon-btn"
                  onClick={onNameSuggest}
                  disabled={suggesting}
                  title="Sugerir nombre automático"
                  aria-label="Sugerir nombre automático"
                >
                  {suggesting ? '…' : <SparklesIcon style={{ width: 16, height: 16 }} />}
                </button>
              </div>
            ) : (
              <div className="v">{form.name || '—'}</div>
            )}
          </div>
          <CampoTexto
            label="Hostname"
            name="hostname"
            value={form.hostname}
            onChange={onChange}
            editing={editing}
            placeholder="san-01.empresa.local"
          />
          <CampoSelect
            label="Fabricante"
            name="manufacturer"
            value={form.manufacturer}
            onChange={onManufacturerChange
              ? (e) => onManufacturerChange(e.target.value)
              : onChange}
            editing={editing}
            options={opcionesFabricante}
            onAdd={onCreateManufacturer}
            addPlaceholder="Nuevo fabricante"
            addLabel="Añadir fabricante al catálogo"
          />
          <CampoSelect
            label="Modelo"
            name="model"
            value={form.model}
            onChange={onChange}
            editing={editing}
            options={opcionesModelo}
            onAdd={onCreateModel}
            addPlaceholder="Nuevo modelo"
            addLabel="Añadir modelo al catálogo"
          />
          <CampoTexto
            label="Nº de serie"
            name="serial_number"
            value={form.serial_number}
            onChange={onChange}
            editing={editing}
            placeholder="SN123456789"
          />
          <CampoTexto
            label="Asset tag"
            name="asset_tag"
            value={form.asset_tag}
            onChange={onChange}
            editing={editing}
            placeholder="AST-0001"
          />
          <CampoSelect
            label="Estado"
            name="status"
            value={form.status}
            onChange={onChange}
            editing={editing}
            options={OPCIONES_ESTADO}
          />
          <CampoSelect
            label="Entorno"
            name="environment"
            value={form.environment}
            onChange={onChange}
            editing={editing}
            options={OPCIONES_ENTORNO}
          />
        </div>
      </div>

      <div className="sf2-card">
        <h3>Ubicación<span className="sf2-count">editable</span></h3>
        <div className="sf2-fields">
          <div className="sf2-field">
            <label>Ubicación</label>
            {editing ? (
              <LocationTreeSelect
                locations={locations}
                value={form.location_id}
                onChange={(id) => onLocationChange({ target: { value: id } })}
                placeholder="— Sin ubicación —"
              />
            ) : (
              <div className="v">
                {form.location_id
                  ? locationPath(buildLocationMap(locations), form.location_id) || '—'
                  : '—'}
              </div>
            )}
          </div>
          <CampoSelect
            label="Rack"
            name="rack_id"
            value={form.rack_id}
            onChange={onRackChange}
            editing={editing}
            options={opcionesRack}
          />
          <CampoTexto
            label="Posición U"
            name="position_u"
            value={form.position_u}
            onChange={onChange}
            editing={editing}
            placeholder="12"
          />
          <CampoTexto
            label="Tamaño U"
            name="size_u"
            value={form.size_u}
            onChange={onChange}
            editing={editing}
            placeholder="2"
          />
          <CampoSelect
            label="Factor de forma"
            name="form_factor"
            value={form.form_factor}
            onChange={onChange}
            editing={editing}
            options={OPCIONES_FORM_FACTOR}
          />
        </div>
        {rangeLabel && (
          <div className="sf2-hint" style={{ marginTop: 10 }}>
            Ocupa {rangeLabel} del rack{rackHeight ? ` (${rackHeight}U en total)` : ''}.
          </div>
        )}
      </div>

      <div className="sf2-card">
        <h3>Gestión</h3>
        <div className="sf2-fields">
          {hasBmcComponent ? (
            <div className="sf2-field">
              <label>IP de gestión</label>
              <div className="v">{form.ip_address || '—'}</div>
              <div className="sf2-hint">
                ✓ Interfaz de gestión (BMC/iLO) creada. La IP se asigna en Red / IP.
              </div>
            </div>
          ) : (
            <div className="sf2-field">
              <label>IP de gestión</label>
              {editing ? (
                <input
                  name="ip_address"
                  value={form.ip_address || ''}
                  onChange={onChange}
                  placeholder="10.0.0.10"
                />
              ) : (
                <div className="v">{form.ip_address || '—'}</div>
              )}
              <div className="sf2-hint">
                {canPromoteBmc ? (
                  <button type="button" className="sf2-link" onClick={onPromoteBmc} disabled={promotingBmc}>
                    {promotingBmc ? 'Creando…' : 'Crear interfaz cableable (BMC / iLO)'}
                  </button>
                ) : (
                  'Campo manual · guarda primero para crear la interfaz'
                )}
              </div>
            </div>
          )}
          <CampoTexto
            label="URL de gestión"
            name="management_url"
            value={form.management_url}
            onChange={onChange}
            editing={editing}
            placeholder="https://san-01:8443"
          />
        </div>
      </div>

      <div className="sf2-card">
        <h3>Tipo y protocolos</h3>
        <div className="sf2-fields">
          <CampoSelect
            label="Tipo de cabina"
            name="storage_flavor"
            value={form.storage_flavor}
            onChange={onChange}
            editing={editing}
            options={OPCIONES_TIPO}
          />
          {form.storage_flavor === 'server_based' && (
            <CampoTexto
              label="SO / Distribución"
              name="os_distribution"
              value={form.os_distribution}
              onChange={onChange}
              editing={editing}
              placeholder="Ubuntu 22.04 + ZFS, Ceph 18…"
            />
          )}
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
            Protocolos soportados
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
            {protocols.map((proto) => {
              const activo = protocolosActivos.includes(proto);
              const pc = protocolColors[proto] || { bg: 'var(--slate-100)', color: 'var(--text-secondary)' };
              return (
                <button
                  key={proto}
                  type="button"
                  className="sf2-chip"
                  onClick={() => onToggleProtocol(proto)}
                  disabled={!editing}
                  aria-pressed={activo}
                  style={{
                    borderColor: activo ? pc.color : 'var(--border)',
                    background: activo ? pc.bg : 'var(--bg-card)',
                    color: activo ? pc.color : 'var(--text-muted)',
                    fontWeight: activo ? 700 : 500,
                  }}
                >
                  {proto}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
